<template>
  <Panel size="small">
    <template #header>
      <span data-cy="heading">Budget</span>
    </template>

    <template #content>
      <!-- CPA Info Banner - shown when category is CPA-only -->
      <b-alert
        v-if="isCpaOnlyCategory"
        show
        variant="info"
        class="mb-3"
      >
        <font-awesome-icon
          :icon="['fas', 'info-circle']"
          class="mr-2"
        />
        <strong>CPA Billing:</strong> This category uses Cost Per Acquisition billing. You will only be charged when a conversion event occurs, not based on call duration.
      </b-alert>

      <!-- Minimum Duration - hidden for CPA categories since billing is conversion-based -->
      <BaseFormToggle
        v-if="!isRTBSelected && !isCpaOnlyCategory"
        id="buyer-create-campaign__audience__minimum-duration"
        v-model="minimumDurationSelected"
        label="Minimum Duration"
        data-cy="toggle-minimum-duration"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        has-tooltip
      >
        <template 
          v-if="minimumDurationSelected"
          #extra
        >          
          <BaseFormInput
            v-model="minimumDurationInSeconds"              
            form-group-class="mb-0"
            type="number"
            max="9999"
            required
            internal-platform-input
            small
            horizontal
            width="7"
            width-lg="5"
            label-class="d-none"
            name="Minimum Duration"
            rules="min_value:0|max_value:9999"
          >
            <template #helpText>
              <b-form-text class="col-1">
                <div>
                  seconds
                </div>
              </b-form-text>
            </template>
          </BaseFormInput>                    
        </template>
        <template 
          v-else
          #helpText          
        >
          <b-badge             
            variant="warning"
            tag="div"
            class="mt-3"
          >
            <font-awesome-icon             
              :icon="['fas', 'exclamation-triangle']"
              :style="{ cursor: 'pointer' }"              
            /> 
            Without minimum call duration limit you pay for every call that connects
          </b-badge>
        </template>
      </BaseFormToggle>
      
      <BaseCurrencyInput
        id="buyer-create-campaign__audience__daily-budget"
        v-model="dailyBudgetInUSD"
        internal-platform-input
        horizontal
        data-cy="input-daily-budget"
        small
        name="Daily Budget"
        placeholder="ex. $1000.00"
        rules="amount_range:10,99999.99"
        label="Daily Budget"
        required
        width="2"
        width-lg="2"
        width-xs="4"
        input-class="fixed-width-130"
        has-tooltip
      />

      <BaseFormToggle        
        id="buyer-create-campaign__audience__hourly-budget-selected"
        v-model="hourlyBudgetSelected"
        label="Hourly Budget"
        data-cy="toggle-hourly-budget"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        has-tooltip
      >
        <template 
          v-if="hourlyBudgetSelected"
          #extra
        >          
          <BaseCurrencyInput
            id="buyer-create-campaign__audience__hourly-budget"
            v-model="hourlyBudgetInUSD"
            internal-platform-input
            horizontal
            label-class="d-none"
            data-cy="input-hourly-budget"
            small
            name="Hourly Budget"   
            placeholder="ex. $100.00"
            rules="amount_range:10,99999.99"         
            required
            width="2"
            width-lg="2"
            width-xs="4"
            input-class="fixed-width-130"            
          />
        </template>
      </BaseFormToggle>            

      <BaseCurrencyInput
        v-if="!isCpaRTBCampaign && (!isProgrammaticSelected || isProgrammaticStaticBid)"
        id="buyer-create-campaign__audience__bid-per-call"
        v-model="maxBidPerCallInUSD"
        :horizontal="true"
        internal-platform-input
        small
        data-cy="input-bid-per-call"
        name="Bid Per Call"
        label="Bid Amount"
        placeholder="ex. $100.00"
        min="0"
        rules="amount_range:0.01,9999.99"
        :max-value="9999.99"
        v-bind="maxBidAttributes"
        width="6"
        width-lg="6"
        width-xs="12"
        input-class="fixed-width-130"
        has-tooltip
        :disabled="isRTBSelected && !isProgrammaticStaticBid"
      >
        <template #helpText>
          <b-form-text v-if="isRTBSelected && !isProgrammaticStaticBid">
            <font-awesome-icon
              :icon="['fas', 'exclamation-triangle']"
              class="mr-1"
            />
            <i>Bid amount and duration will come from the bids in Ringba RTB.</i>
          </b-form-text>
        </template>
      </BaseCurrencyInput>

      <!-- CPA Conversion Toggle - only shown for CPA-supporting categories -->
      <BaseFormToggle
        v-if="isCpaOnlyCategory"
        id="buyer-create-campaign__budget__cpa-conversion"
        v-model="isPostbackConversion"
        label="CPA Conversion"
        data-cy="toggle-cpa-conversion"
        internal-platform-input
        :checked-value="true"
        :unchecked-value="false"
        disabled
        has-tooltip
      >
        <template #helpText>
          <b-form-text class="mt-3">
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-1"
            />
            <i>Required for this category - billing is based on CPA conversions only.</i>
          </b-form-text>
        </template>
      </BaseFormToggle>

      <!-- Default Postback Bid Amount - only shown for CPA categories with RingbaRTB routing -->
      <BaseCurrencyInput
        v-if="isCpaRTBCampaign"
        id="buyer-create-campaign__budget__default-postback-bid-amount"
        v-model="defaultPostbackBidAmount"
        :horizontal="true"
        internal-platform-input
        small
        data-cy="input-default-postback-bid-amount"
        name="Bid Amount"
        label="Bid Amount"
        placeholder="ex. $100.00"
        rules="amount_range:0.01,9999.99"
        :max-value="9999.99"
        required
        width="6"
        width-lg="6"
        width-xs="12"
        input-class="fixed-width-130"
        has-tooltip
      >
        <template #helpText>
          <b-form-text>
            <font-awesome-icon
              :icon="['fas', 'info-circle']"
              class="mr-1"
            />
            <i>The static bid amount for postback conversions. Currently only static bids are supported for CPA conversions.</i>
          </b-form-text>
        </template>
      </BaseCurrencyInput>

      <b-row>
        <b-col md="3" />
        <b-col
          v-if="submitted && invalid"
          class="text-danger"
        >
          Please fix the errors in your form
        </b-col>
      </b-row>

      <BaseButtonLoader
        :horizontal="true"
        data-cy="button-create-campaign"
        internal-platform-input
        variant="primary"
        :loading="loading"
        :disabled="!minimumInitialDepositSatisfied"
        type="submit"
      >
        {{ edit ? 'Save' : 'Create' }} Campaign
      </BaseButtonLoader>
    </template>
  </Panel>
</template>

<script>

import { currencyFormatter } from '@/utils/formatCurrency'
import { mapState, mapMutations } from 'vuex'

export default {
  name: 'Budget',
  props: {
    invalid: {
      type: Boolean,
      default: true
    },

    edit: {
      type: Boolean,
      default: false
    },

    loading: {
      type: Boolean,
      default: false
    },

    minimumInitialDepositSatisfied: {
      type: Boolean,
      default: false
    },

    selectedCategory: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      submitted: false,
    }
  },

  computed: {
    ...mapState('campaign', ['buyerCampaign']),

    isRTBSelected() {
      return this.buyerCampaign?.routing?.type === 'RingbaRTB' || this.buyerCampaign?.routing?.type === 'Programmatic'
    },

    isProgrammaticSelected() {
      return this.buyerCampaign?.routing?.type === 'Programmatic'
    },

    isProgrammaticStaticBid() {
      return this.isProgrammaticSelected && this.buyerCampaign?.routing?.programmaticTarget?.bidSettings?.bidType === 'Static'
    },

    maxBidAttributes() {
      return (!this.isRTBSelected || this.isProgrammaticStaticBid) ? { required: '' } : {}
    },

    minimumDurationSelected: {
      get() {
        return this.buyerCampaign.budget.minimumDurationSelected
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.minimumDurationSelected = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    minimumDurationInSeconds: {
      get() {
        return this.buyerCampaign.budget.minimumDurationInSeconds
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.minimumDurationInSeconds = Number(value)
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    dailyBudgetInUSD: {
      get() {
        return this.buyerCampaign.budget.dailyBudgetInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.dailyBudgetInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    maxBidPerCallInUSD: {
      get() {
        return this.buyerCampaign.budget.maxBidPerCallInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.maxBidPerCallInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    hourlyBudgetSelected: {
      get() {
        return this.buyerCampaign.budget.hourlyBudgetSelected
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.hourlyBudgetSelected = value
        budget.hourlyBudgetInUSD = currencyFormatter(budget.hourlyBudgetInUSD)
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    hourlyBudgetInUSD: {
      get() {
        return this.buyerCampaign.budget.hourlyBudgetInUSD
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.hourlyBudgetInUSD = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    isCpaOnlyCategory() {
      return this.selectedCategory?.isCpaConversionOnly === true
    },

    isCpaRTBCampaign() {
      return this.isCpaOnlyCategory && this.buyerCampaign?.routing?.type === 'RingbaRTB'
    },

    isPostbackConversion: {
      get() {
        return this.buyerCampaign.budget.isPostbackConversion
      },
      set(value) {
        const budget = { ...this.buyerCampaign.budget }
        budget.isPostbackConversion = value
        this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
      }
    },

    defaultPostbackBidAmount: {
      get() {
        return this.buyerCampaign.routing.ringbaRTB?.defaultPostbackBidAmount
      },
      set(value) {
        const routing = {
          ...this.buyerCampaign.routing,
          ringbaRTB: {
            ...this.buyerCampaign.routing.ringbaRTB,
            defaultPostbackBidAmount: value
          }
        }
        this.UPDATE_BUYER_CAMPAIGN_ROUTING(routing)
      }
    },
  },

  watch: {
    isCpaOnlyCategory: {
      immediate: true,
      handler(isCpaOnly) {
        if (isCpaOnly && !this.buyerCampaign.budget.isPostbackConversion) {
          const budget = { ...this.buyerCampaign.budget, isPostbackConversion: true }
          this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
        } else if (!isCpaOnly && this.buyerCampaign.budget.isPostbackConversion) {
          const budget = { ...this.buyerCampaign.budget, isPostbackConversion: false }
          this.UPDATE_BUYER_CAMPAIGN_BUDGET(budget)
        }
      }
    }
  },

  methods: {
    ...mapMutations('campaign', ['UPDATE_BUYER_CAMPAIGN_BUDGET', 'UPDATE_BUYER_CAMPAIGN_ROUTING']),

    submit() {
      this.submitted = true
      if (!this.invalid) {
        this.$emit('submit')
      }
    },
  },
}
</script>

<style lang="scss" scoped></style>
